import React from "react";
import { Box } from "@mui/material";
import { ProjectDetailPageProps } from "../../../types";
import { useFrameworkData } from "./hooks/useFrameworkData";
import { useProjectStatusData } from "./hooks/useProjectStatusData";
import PageTemplate from "../../layout/PageTemplate";
import MasterDataSection from "./sections/MasterDataSection";
import PlanningPermissionSection from "./sections/PlanningPermissionSection";
import ProjectStatusSection from "./sections/ProjectStatusSection";
import StandortSection from "./sections/StandortSection";
import DeadlinesSection from "../deadlines/DeadlinesSection";
import { useProjectDeadlines } from "../../../hooks/useProjectDeadlines";
import { useAuth } from "../../../auth/AuthProvider";

interface ProjectOverviewPageProps extends ProjectDetailPageProps {
  canView: boolean;
  canEdit: boolean;
  onProjectUpdated?: () => void;
}

const ProjectOverviewPage: React.FC<ProjectOverviewPageProps> = ({
  project,
  canEdit,
  onProjectUpdated,
}) => {
  const { isLoading, isSaving, error, fieldErrors, planOptions, updateField } =
    useFrameworkData({ project, onProjectUpdated });
  const { user } = useAuth();
  const deadlines = useProjectDeadlines(project?.id);

  const {
    isLoading: isStatusLoading,
    isSaving: isStatusSaving,
    error: statusError,
    options: statusOptions,
    gesamtstatus,
    gesamtstatusDomain,
    gesamtstatusAbgeschlossen,
    flaechenakquiseStatus,
    finanzierungStatus,
    updateFlaechenakquiseStatus,
    updatePlanungsrechtStatus,
    updateBimschgStatus,
    updateEegAusschreibungStatus,
    updateFinanzierungStatus,
    updateBauStatus,
  } = useProjectStatusData({ project, onProjectUpdated });

  return (
    <PageTemplate
      isLoading={isLoading || isStatusLoading}
      isSaving={isSaving || isStatusSaving}
      saveError={error || statusError}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "3fr 2fr" },
          gap: 3,
          alignItems: "start",
        }}
      >
        {/* Linke Spalte: Projektstatus + Stammdaten */}
        {/* Kein gap hier: Section bringt bereits mb:3 als Abstand zum nächsten Geschwister mit */}
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <ProjectStatusSection
            gesamtstatus={gesamtstatus}
            gesamtstatusDomain={gesamtstatusDomain}
            gesamtstatusAbgeschlossen={gesamtstatusAbgeschlossen}
            flaechenakquiseStatus={flaechenakquiseStatus}
            planungsrechtStatus={project.planungsrecht_status ?? ""}
            bimschgStatus={project.bimschg_status ?? ""}
            eegAusschreibungStatus={project.eeg_ausschreibung_status ?? ""}
            finanzierungStatus={finanzierungStatus}
            bauStatus={project.bau_status ?? ""}
            flaechenakquiseOptions={statusOptions.flaechenakquise}
            planungsrechtOptions={statusOptions.planungsrecht}
            bimschgOptions={statusOptions.bimschg}
            eegAusschreibungOptions={statusOptions.eegAusschreibung}
            finanzierungOptions={statusOptions.finanzierung}
            bauOptions={statusOptions.bau}
            canEdit={canEdit}
            onFlaechenakquiseChange={updateFlaechenakquiseStatus}
            onPlanungsrechtChange={updatePlanungsrechtStatus}
            onBimschgChange={updateBimschgStatus}
            onEegAusschreibungChange={updateEegAusschreibungStatus}
            onFinanzierungChange={updateFinanzierungStatus}
            onBauChange={updateBauStatus}
          />

          <MasterDataSection
            projektname={project.name}
            baubeginn={project.baubeginn ?? ""}
            inbetriebnahme={project.inbetriebnahme ?? ""}
            laufzeitJahre={project.laufzeit_jahre ?? 20}
            canEdit={canEdit}
            fieldErrors={fieldErrors}
            onFieldChange={updateField}
          />

          <PlanningPermissionSection
            regionalplan={project.regionalplan}
            flaechennutzungsplan={project.flaechennutzungsplan}
            sonstigesPlanungsverfahren={project.sonstiges_planungsverfahren}
            planOptions={planOptions}
            canEdit={canEdit}
            onFieldChange={updateField}
          />
        </Box>

        {/* Rechte Spalte: Karte */}
        {/* Kein gap hier: Section bringt bereits mb:3 als Abstand zum nächsten Geschwister mit */}
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <StandortSection
            projectId={project.id}
            projectName={project.name}
            bundesland={project.bundesland}
            landkreis={project.landkreis}
            gemeinde={project.gemeinde}
            gewerbesteuerhebesatz={project.gewerbesteuerhebesatz ?? 400}
            istSuedregion={project.ist_suedregion ?? false}
            canEdit={canEdit}
            onFieldChange={updateField}
          />
        </Box>
      </Box>

      {/* Über die volle Breite: alle Termine des Projekts samt Bezug (ADR 010) */}
      <DeadlinesSection
        projectId={project.id}
        controller={deadlines}
        canEdit={canEdit}
        withScopeToggle
        currentUserId={user?.id}
      />
    </PageTemplate>
  );
};

export default ProjectOverviewPage;
